"use client";

import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

/**
 * Positioned slots for the chrome that floats over the map.
 *
 * The overlay is a single layout that reads its children by slot rather than a set
 * of absolutely-positioned boxes: the top row and the bottom row have to share the
 * available height without overlapping, and the three bottom cells each take a
 * third so a wide control in one can't push another off-centre.
 *
 * Everything is `pointer-events-none` by default — the overlay covers the whole
 * map, and re-enabling pointer events per control is what keeps the map draggable
 * through the gaps between them.
 */
interface SlotProps {
  children: ReactNode;
}

export function MapControlTop({ children }: SlotProps) {
  return <div className="pointer-events-none">{children}</div>;
}

export function MapControlBottomLeft({ children }: SlotProps) {
  return <div className="pointer-events-none p-3">{children}</div>;
}

export function MapControlBottomCenter({ children }: SlotProps) {
  return <div className="pointer-events-none">{children}</div>;
}

export function MapControlBottomRight({ children }: SlotProps) {
  return <div className="pointer-events-none p-3">{children}</div>;
}

interface MapControlsProps {
  className?: string;
  top?: ReactNode;
  bottomLeft?: ReactNode;
  bottomCenter?: ReactNode;
  bottomRight?: ReactNode;
}

/**
 * Slots are named props rather than children inspected by `displayName`, which is
 * how the legacy version found them. Reading child types breaks the moment a slot
 * is wrapped — in a fragment, a conditional, or a component that forwards it — and
 * it fails by silently rendering nothing rather than by erroring.
 */
export function MapControls({
  className,
  top,
  bottomLeft,
  bottomCenter,
  bottomRight,
}: MapControlsProps) {
  return (
    <div
      className={cn(
        "pointer-events-none flex min-w-0 flex-col justify-stretch overflow-hidden relative",
        className,
      )}
    >
      {top}
      <div className="flex flex-auto items-end justify-between">
        <div className="flex min-w-0 basis-1/3 flex-col items-start justify-end">
          {bottomLeft}
        </div>
        <div className="flex min-w-0 basis-1/3 flex-col items-center justify-end">
          {bottomCenter}
        </div>
        <div className="flex min-w-0 basis-1/3 flex-col items-end justify-end">
          {bottomRight}
        </div>
      </div>
    </div>
  );
}
